import { useConfiguratorStateDomain } from "@/configurator/domain/hooks/useConfiguratorStateDomain";
import {
  findActiveModelOption,
  findProduct,
} from "@/configurator/domain/logic/catalog";
import {
  buildModelOptionViews,
  buildOptionGroupViews,
} from "@/configurator/domain/logic/optionViews";
import type { Id } from "@/configurator/domain/models/types";
import type {
  ModelOptionView,
  OptionGroupView,
} from "@/configurator/domain/models/view";
import type { ConfiguratorSnapshot } from "@/configurator/domain/ports/configuratorState";

// Module scope keeps the identity stable — see the note in useConfiguratorDomain.
const selectOptionGroups = (s: ConfiguratorSnapshot) =>
  buildOptionGroupViews(
    findProduct(s.config.catalog, s.activeProductId),
    s.selectedOptions,
  );

const selectModels = (s: ConfiguratorSnapshot) =>
  buildModelOptionViews(
    s.config.model.options,
    findActiveModelOption(s.config, s.selectedModelId)?.id,
  );

export type OptionsDomain = {
  groups: OptionGroupView[];
  models: ModelOptionView[];
  pickOption: (groupId: Id, optionId: Id) => void;
  toggleOption: (groupId: Id, optionId: Id) => void;
  pickModel: (modelId: Id) => void;
};

/**
 * The options panel's rows and its three writes.
 *
 * The rows arrive resolved — which option is on, what colour it paints — so the
 * panel never asks the catalog a question of its own: presentation may not call
 * `domain/logic`, and a component that re-derives a selection from group kinds
 * is how the panel and the renderer drift apart.
 */
export function useOptionsDomain(): OptionsDomain {
  const { useSelect, commands } = useConfiguratorStateDomain();
  const groups = useSelect(selectOptionGroups);
  const models = useSelect(selectModels);

  return {
    groups,
    models,
    pickOption: (groupId, optionId) =>
      commands.setOption({ groupId, optionId }),
    toggleOption: (groupId, optionId) =>
      commands.toggleOption({ groupId, optionId }),
    pickModel: commands.setActiveModel,
  };
}
